import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { axiosPost } from "../axios/config";
import { setToken } from "../redux/token/token.action";
import { setCurrentUser } from "../redux/user/user.action";

// Captured once when the page first loads, before any effects run or
// the URL gets cleaned up. This is the only reliable way to check
// whether cognito_token was present on the initial page load.
export const hadCognitoTokenOnLoad = new URLSearchParams(window.location.search).has("cognito_token");

// Kept for use in useState initializer inside the hook itself
export const hasCognitoTokenInUrl = () => {
	return new URLSearchParams(window.location.search).has("cognito_token");
};
// Removes cognito_token from the URL without adding a new browser history entry.
// This means the token will not appear if the user hits the back button.
const removeCognitoTokenFromUrl = () => {
	const params = new URLSearchParams(window.location.search);
	params.delete("cognito_token");

	const newSearch = params.toString();
	const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");

	window.history.replaceState({}, "", newUrl);
};

// Handles the case where a user arrives from core.cresa.com with a
// cognito_token in the URL. Exchanges it for a Bluechip session token
// silently, with no login screen or redirect required.
//
// Returns { isExchanging: boolean } so the caller can show a loader
// while the exchange is in flight.
const useCognitoTokenExchange = () => {
	const [isExchanging, setIsExchanging] = useState(hasCognitoTokenInUrl());

	const dispatch = useDispatch();
	useEffect(() => {
		const cognitoToken = new URLSearchParams(window.location.search).get("cognito_token");

		// Nothing to do if there is no cognito_token in the URL
		if (!cognitoToken) {
			return;
		}

		// Strip the token from the URL immediately -- before any async work --
		// so it does not sit in browser history or retrigger on the next render
		removeCognitoTokenFromUrl();

		const exchangeToken = async () => {
			setIsExchanging(true);

			try {
				const response = await axiosPost("/api/v1/auth/cognito/token-exchange", {
					cognito_token: cognitoToken,
				});

				const { status = "error", data = {} } = response ?? {};
				const { token = null, user = null } = data;

				if (status !== "error" && token && user) {
					// Dispatch user first, then token. This prevents App.jsx from
					// firing getUser() in the window between the two dispatches,
					// which would cause a redundant and potentially conflicting API call.
					dispatch(setCurrentUser(user));
					dispatch(setToken(token));
				} else {
					// Token was invalid or the backend rejected it -- go to login silently
					window.location.replace("/login");
				}
			} catch (error) {
				window.location.replace("/login");
			} finally {
				setIsExchanging(false);
			}
		};

		exchangeToken();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { isExchanging };
};

export default useCognitoTokenExchange;
